import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Wrench, Loader2, Search } from 'lucide-react';
import { assetService } from '../../../../services/activos/assetService';
import { productService } from '../../../../services/productos/productService';
import { sparePartsService } from '../../services/sparePartsService';

export const ExecutionModalSpareParts = ({
  workOrder,
  spareParts = [],
  queryClient,
  formRef,
  isCompleted
}) => {
  const [localModalError, setLocalModalError] = useState(null);

  // ── Spare Part Consumption Form State ──────────────────────────────────────
  const [consumptionFormData, setConsumptionFormData] = useState({
    productId: '',
    assetId: '',
    quantity: '',
    reason: workOrder?.title || ''
  });

  const [productSearch, setProductSearch] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [availableAssets, setAvailableAssets] = useState([]);
  const [isRegisteringConsumption, setIsRegisteringConsumption] = useState(false);

  const { data: products = [], isLoading: isLoadingProducts } = useQuery({
    queryKey: ['products'],
    queryFn: () => productService.getProducts()
  });

  const { data: assets = [], isLoading: isLoadingAssets } = useQuery({
    queryKey: ['assets', consumptionFormData.productId],
    queryFn: () => assetService.getAssetsByProduct(consumptionFormData.productId),
    enabled: !!consumptionFormData.productId
  });

  const { data: assetParent, isLoading: isLoadingParent } = useQuery({
    queryKey: ['assetParent'],
    queryFn: () => assetService.getAssetHierarchy(workOrder.asset_id),
    enabled: !!workOrder.asset_id
  });

  useEffect(() => {
    if (!productSearch.trim()) {
      setFilteredProducts(products);
    } else {
      const lower = productSearch.toLowerCase();
      setFilteredProducts(products.filter(p =>
        p.name?.toLowerCase().includes(lower) ||
        p.product_type?.toLowerCase().includes(lower) ||
        p.family?.toLowerCase().includes(lower)
      ));
    }
  }, [productSearch]);

  useEffect(() => {
    if (!consumptionFormData.productId) {
      setAvailableAssets([]);
      setConsumptionFormData(prev => ({ ...prev, assetId: '' }));
      return;
    }
    console.log(assets, 'Assets')
    console.log(assetParent, 'AssetParent')
    const assetsInOrder = assets.filter(a =>
      (assetParent.HierarchicalLevel.every(level => a.HierarchicalLevel.includes(level)) && a.HierarchicalLevel.length > assetParent.HierarchicalLevel.length) ||
      (assetParent.HierarchicalLevel.slice(0, 2)?.every(level => a.HierarchicalLevel.includes(level) && a.HierarchicalLevel.slice(0, 3).includes('0_Common')))
    );
    console.log(assetsInOrder);
    setAvailableAssets(assetsInOrder || []);
    if (assetsInOrder && assetsInOrder.length > 0) {
      setConsumptionFormData(prev => ({ ...prev, assetId: assetsInOrder[0].ID || assetsInOrder[0].id }));
    } else {
      setConsumptionFormData(prev => ({ ...prev, assetId: '' }));
    }
  }, [consumptionFormData.productId, isLoadingAssets]);

  const handleRequiredPartClick = (part) => {
    setConsumptionFormData(prev => ({
      ...prev,
      productId: part.product_id,
      quantity: part.quantity_required ?? part.quantity ?? 1
    }));
    setProductSearch(part.product?.Name || '');
  };

  const handleRegisterConsumption = async () => {
    console.log(consumptionFormData)
    if (!consumptionFormData.productId || !consumptionFormData.assetId || !consumptionFormData.quantity) {
      setLocalModalError("Please select a product, asset, and quantity to register consumption.");
      formRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (consumptionFormData.quantity <= 0 || consumptionFormData.quantity > 999999999.999999) {
      setLocalModalError("Please enter a valid quantity to register consumption.");
      formRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (spareParts.find(p => p.product_id === consumptionFormData.productId)?.unit === 'units' && !Number.isInteger(parseFloat(consumptionFormData.quantity))) {
      setLocalModalError("The quantity entered must be an integer for this product.");
      formRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const confirmationDialogue = window.confirm("Are you sure you want to register this consumption? This act cannot be undone.");
    if (!confirmationDialogue) {
      return;
    }

    setIsRegisteringConsumption(true);
    setLocalModalError(null);
    try {
      const payload = {
        spare_part_asset_id: parseInt(consumptionFormData.assetId, 10),
        product_id: parseInt(consumptionFormData.productId, 10),
        quantity: parseFloat(consumptionFormData.quantity),
        reason: consumptionFormData.reason
      };

      await sparePartsService.registerConsumption(workOrder.id, payload);

      // Clear form on success
      setConsumptionFormData({
        productId: '',
        assetId: '',
        quantity: '',
        reason: workOrder?.title || ''
      });
      setProductSearch('');
      setAvailableAssets([]);

      // Force refresh data
      queryClient.invalidateQueries({ queryKey: ['wo'] });
    } catch (err) {
      setLocalModalError("Failed to register consumption. " + (err.response?.data?.message || err.message));
      formRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsRegisteringConsumption(false);
    }
  };

  return (
    <div className="space-y-4">
      {localModalError && (
        <div className="rounded-lg border border-red-800 bg-red-900/20 p-4">
          <p className="text-red-300 text-sm">{localModalError}</p>
        </div>
      )}

      <h3 className="text-lg font-semibold text-white border-b border-gray-700 pb-2">
        Spare Parts
      </h3>
      {spareParts.length > 0 ? (
        <div className="rounded-lg border border-amber-700/50 bg-amber-900/10 p-4">
          <div className="flex items-center gap-2 mb-3">
            <Wrench className="w-4 h-4 text-amber-400 flex-shrink-0" />
            <p className="text-sm font-semibold text-amber-300">Spare Parts Required</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {[...spareParts].sort((a, b) => {
              const nameA = (a.product?.Name || a.product?.name || `Part #${a.product_id || 0}`).toLowerCase();
              const nameB = (b.product?.Name || b.product?.name || `Part #${b.product_id || 0}`).toLowerCase();
              return nameA.localeCompare(nameB);
            }).map((part, idx) => (
              <div
                key={part.id ?? idx}
                onClick={() => handleRequiredPartClick(part)}
                className="flex items-center gap-2 bg-gray-700/60 border border-gray-600 rounded-lg px-3 py-2 cursor-pointer hover:bg-gray-600/60 transition-colors"
              >
                <span className="text-white text-sm font-medium">
                  {part.product?.Name ?? `Part #${part.product_id ?? idx + 1}`}
                </span>
                <span className="text-gray-400 text-xs">—</span>
                <span className="text-amber-300 text-sm font-mono font-semibold">
                  {part.quantity_required ?? part.quantity}
                </span>
                {part.unit && (
                  <span className="text-gray-400 text-xs">{part.unit}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-12 text-gray-400">
          <Wrench className="w-12 h-12 mb-3 opacity-40" />
          <p className="text-sm">No spare parts required for this task</p>
        </div>
      )}

      {/* Used Spare Parts */}
      {workOrder?.used_spare_parts && workOrder.used_spare_parts.length > 0 && (() => {
        const requiredProductIds = new Set(spareParts.map(p => p.product_id));
        const planned = [];
        const unplanned = [];

        workOrder.used_spare_parts.forEach(part => {
          if (requiredProductIds.has(part.product_id)) {
            planned.push(part);
          } else {
            unplanned.push(part);
          }
        });

        const sortFn = (a, b) => (a.product_name || '').localeCompare(b.product_name || '');
        planned.sort(sortFn);
        unplanned.sort(sortFn);

        return (
          <div className="mt-4 rounded-lg border border-green-700/50 bg-green-900/10 p-4">
            <div className="flex items-center gap-2 mb-3">
              <Wrench className="w-4 h-4 text-green-400 flex-shrink-0" />
              <p className="text-sm font-semibold text-green-300">Used Spare Parts</p>
            </div>
            <div className="flex flex-col gap-2">
              {planned.map((part) => (
                <div
                  key={part.id}
                  className="flex items-center justify-between bg-gray-700/60 border border-gray-600 rounded-lg px-3 py-2"
                >
                  <div className="flex flex-col">
                    <span className="text-white text-sm font-medium">
                      {part.product_name}
                    </span>
                    <span className="text-gray-400 text-xs">
                      {part.spare_part_code ? `Asset: ${part.spare_part_code}` : 'No asset specified'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400 text-xs">Qty:</span>
                    <span className={`text-sm font-mono font-semibold ${part.quantity > spareParts.find(p => p.product_id === part.product_id)?.quantity_required ? 'text-amber-400' : 'text-green-300'}`}>
                      {part.quantity} {spareParts.find(p => p.product_id === part.product_id)?.unit}
                    </span>
                  </div>
                </div>
              ))}

              {unplanned.length > 0 && (
                <>
                  {planned.length > 0 && <div className="border-t border-green-800/50 my-2" />}
                  <div className="text-xs font-semibold text-amber-500 uppercase tracking-wider mb-1 mt-1">
                    Unplanned / Additional Parts
                  </div>
                  {unplanned.map((part) => (
                    <div
                      key={part.id}
                      className="flex items-center justify-between bg-gray-700/60 border border-amber-700/50 rounded-lg px-3 py-2"
                    >
                      <div className="flex flex-col">
                        <span className="text-amber-100 text-sm font-medium">
                          {part.product_name}
                        </span>
                        <span className="text-gray-400 text-xs">
                          {part.spare_part_code ? `Asset: ${part.spare_part_code}` : 'No asset specified'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-400 text-xs">Qty:</span>
                        <span className="text-amber-400 text-sm font-mono font-semibold">
                          {part.quantity}
                        </span>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        );
      })()}

      {/* Manual Consumption Registration Form */}
      {!isCompleted && (
        <div className="mt-8 rounded-lg border border-gray-600 bg-gray-750 p-5">
          <h4 className="text-md font-semibold text-white mb-4">Register Used Spare Part</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* Product Autocomplete / Select */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Search Product <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  value={productSearch}
                  onChange={e => setProductSearch(e.target.value)}
                  placeholder="Type to search..."
                  className="w-full pl-9 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500"
                />
              </div>
              {productSearch.trim() && !consumptionFormData.productId && (
                <div className="absolute z-10 w-full mt-1 bg-gray-700 border border-gray-600 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {isLoadingProducts ? (
                    <div className="p-3 text-sm text-gray-400 text-center flex items-center justify-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" /> Loading...
                    </div>
                  ) : filteredProducts.length === 0 ? (
                    <div className="p-3 text-sm text-gray-400 text-center">No products found</div>
                  ) : (
                    filteredProducts.map(p => (
                      <div
                        key={p.id}
                        className="p-3 text-sm text-white hover:bg-gray-600 cursor-pointer border-b border-gray-600/50 last:border-0"
                        onClick={() => {
                          setConsumptionFormData(prev => ({ ...prev, productId: p.id }));
                          setProductSearch(p.name);
                        }}
                      >
                        <div className="font-medium">{p.name}</div>
                        <div className="text-xs text-gray-400">Type: {p.product_type} | Family: {p.family}</div>
                      </div>
                    ))
                  )}
                </div>
              )}
              {/* Clear selected product */}
              {consumptionFormData.productId && (
                <button
                  type="button"
                  onClick={() => {
                    setConsumptionFormData(prev => ({ ...prev, productId: '', assetId: '' }));
                    setProductSearch('');
                  }}
                  className="absolute right-3 top-[34px] text-xs text-blue-400 hover:text-blue-300"
                >
                  Clear
                </button>
              )}
            </div>

            {/* Asset Select */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Select Specific Asset <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <select
                  value={consumptionFormData.assetId}
                  onChange={e => setConsumptionFormData(prev => ({ ...prev, assetId: e.target.value }))}
                  disabled={!consumptionFormData.productId || isLoadingAssets}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500 disabled:opacity-50 appearance-none"
                >
                  <option value="">
                    {!consumptionFormData.productId ? 'Select a product first' :
                      isLoadingAssets ? 'Loading assets...' : 'Select an asset...'}
                  </option>
                  {availableAssets.map(a => (
                    <option key={a.ID || a.id} value={a.ID || a.id}>
                      {a.Code} - {a.TechCode}
                    </option>
                  ))}
                </select>
                {isLoadingAssets && (
                  <Loader2 className="absolute right-3 top-2.5 h-4 w-4 text-blue-400 animate-spin" />
                )}
              </div>
              {consumptionFormData.productId && !isLoadingAssets && availableAssets.length === 0 && (
                <p className="text-xs text-amber-400 mt-1">No assets available for this product.</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Quantity <span className="text-red-400">*</span>
              </label>
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={consumptionFormData.quantity}
                onChange={e => setConsumptionFormData(prev => ({ ...prev, quantity: e.target.value }))}
                placeholder="e.g. 1.5"
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Reason / Observation
              </label>
              <input
                type="text"
                value={consumptionFormData.reason}
                onChange={e => setConsumptionFormData(prev => ({ ...prev, reason: e.target.value }))}
                placeholder="Optional detail"
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>

          <div className="flex justify-end mt-4">
            <button
              type="button"
              onClick={handleRegisterConsumption}
              disabled={isRegisteringConsumption || !consumptionFormData.productId || !consumptionFormData.assetId || !consumptionFormData.quantity}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {isRegisteringConsumption ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</>
              ) : (
                'Register Usage'
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
